import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { Icon, TextField, Button } from '@material-ui/core';
import ConfirmDeleteUser from './ConfirmDeleteUser';
import ConfirmDisableUser from './ConfirmDisableUser';
import ConfirmEnableUser from './ConfirmEnableUser';
import { cleanUser, chooseUser, loadUsers } from '../../redux/actions/usersActions';

function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const headRows = [
    { id: 'fullName', numeric: false, disablePadding: false, label: 'Name' },
    { id: 'email', numeric: false, disablePadding: false, label: 'E-mail' },
    { id: 'phoneNumber', numeric: false, disablePadding: false, label: 'Phone Number' },
    { id: 'zipCode', numeric: false, disablePadding: false, label: 'Zip Code' },
    { id: 'role', numeric: false, disablePadding: false, label: 'Role' },
    { id: 'actions', numeric: false, disablePadding: false, label: 'Actions' }
];

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headRows.map(row => (
                    <TableCell
                        key={row.id}
                        align={row.numeric ? 'right' : 'left'}
                        padding={row.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === row.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === row.id}
                            direction={order}
                            onClick={createSortHandler(row.id)}
                        >
                            {row.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
    },
    title: {
        flex: '0 0 auto',
    },
}));

const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles();
    const { numSelected } = props;

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            <div className={classes.title}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subtitle1">
                        {numSelected} selected
          </Typography>
                ) : (
                        <Typography variant="h6" id="tableTitle">
                            Users
          </Typography>
                    )}
            </div>
            <div className={classes.spacer} />
            <div className={classes.actions}>
                <Tooltip title="Add new Administrator">
                    <IconButton color="secondary" aria-label="Add new Administrator" onClick={props.addUser}>
                        <Icon>add</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    search: {
        margin: theme.spacing(2),
        width:'50%'
    }
}));

function UsersTable(props) {
    const classes = useStyles();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
    const [showEnableConfirmatiokn, setShowEnableConfirmation] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('fullName');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');

    function showModalDelete(user) {
        setShowDeleteConfirmation(true);
        setSelectedUser(user);
    }

    function closeModalDelete() {
        setShowDeleteConfirmation(false);
        setSelectedUser(null);
    }

    function showModalDisable(user) {
        setShowDisableConfirmation(true);
        setSelectedUser(user);
    }

    function closeModalDisable() {
        setShowDisableConfirmation(false);
        setSelectedUser(null);
    }

    function showModalEnable(user) {
        setShowEnableConfirmation(true);
        setSelectedUser(user);
    }

    function closeModalEnable() {
        setShowEnableConfirmation(false);
        setSelectedUser(null);
    }

    function handleRequestSort(event, property) {
        const isDesc = orderBy === property && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(property);
    }

    function handleSelectAllClick(event) {
        if (event.target.checked) {
            const newSelecteds = listUsers.map(n => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    }

    function handleChangePage(event, newPage) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event) {
        setRowsPerPage(+event.target.value);
        setPage(0);
    }



    // Helper function to normalize user data structure
    function normalizeUserData(users) {
        if (users.length > 0) {
            console.log('Raw user data sample:', users[0]);
        }
        
        return users.map(user => ({
            ...user,
            role: user.Role || user.role, // Fallback to 'USER' if no role is found
            isActivated: user.isActivated !== undefined ? user.isActivated : true // Default to activated if not specified
        }));
    }

    const listUsers = useMemo(() => {
        const normalizedUsers = normalizeUserData(props.users || []);
        if(search === ''){
            return normalizedUsers;
        } else {
            return normalizedUsers.filter((user) => user.fullName && user.fullName.toUpperCase().includes(search.toUpperCase()));
        }
    }, [props.users, search]);

    const isSelected = name => selected.indexOf(name) !== -1;

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, listUsers.length - page * rowsPerPage);

    // useEffect(() => {
    //     if(listUsers.length === 0){
    //         const load = loadUsers(props.users);
    //     }
    // }, [listUsers]);

    return (
        <div className={classes.root}>
            <Paper style={{ backgroundColor: 'white' }}>
                <EnhancedTableToolbar numSelected={selected.length} addUser={props.addUser} />
                <div className={classes.tableWrapper}>
                    <TextField 
                        variant="outlined" 
                        label="Search"
                        className={classes.search}
                        onChange={({ target }) => setSearch(target.value)}
                    />
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={listUsers.length}
                        />
                        <TableBody>
                            {stableSort(listUsers, getSorting(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.fullName);
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.id}
                                            selected={isItemSelected}>
                                            <TableCell component="th" id={labelId} scope="row">
                                                {row.fullName}
                                            </TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.phoneNumber}</TableCell>
                                            <TableCell>{row.zipCode}</TableCell>
                                            <TableCell>{row.role}</TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    {/* Edit Button */}
                                                    <Tooltip title="Edit user" aria-label="Edit user">
                                                        <IconButton onClick={() => {
                                                            props.chooseUser(row);
                                                            props.addUser();
                                                        }}>
                                                            <Icon color="primary">edit</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    {/* Delete Button */}
                                                    <Tooltip title="Delete user" aria-label="Delete user">
                                                        <IconButton onClick={() => {
                                                            showModalDelete(row);
                                                        }}>
                                                            <Icon color="secondary">delete</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    {/* Enable/Disable Button */}
                                                    {row.isActivated ?
                                                        <Tooltip title="Disable">
                                                            <IconButton color="secondary" 
                                                                        aria-label="Disable"
                                                                        onClick={() =>{
                                                                            showModalDisable(row);
                                                                        }}>
                                                                <Icon>block</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                        :
                                                        <Tooltip title="Enable">
                                                            <IconButton color="primary" 
                                                                        aria-label="Enable"
                                                                        onClick={() => {
                                                                            showModalEnable(row);
                                                                        }}
                                                                        >
                                                                <Icon>check</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 49 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <TablePagination
                    rowsPerPageOptions={[5, 10]}
                    component="div"
                    count={listUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    backIconButtonProps={{
                        'aria-label': 'Previous Page',
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'Next Page',
                    }}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>
            <ConfirmDeleteUser showConfirm={showDeleteConfirmation}
                user={selectedUser}
                onClose={() => {
                    closeModalDelete();
                }} 
            />
            <ConfirmDisableUser showConfirm={showDisableConfirmation}
                user={selectedUser}
                onClose={() => {
                    closeModalDisable();
                }}
            />
            <ConfirmEnableUser showConfirm={showEnableConfirmatiokn}
                user={selectedUser}
                onClose={() => {
                    closeModalEnable();
                }}
            />
        </div>
    );
}


const mapDispatchToProps = dispatch => (
    bindActionCreators({ cleanUser, chooseUser, loadUsers }, dispatch)
);

export default connect(null, mapDispatchToProps)(UsersTable);